import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Filter, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ConnectionsAnimation } from '../animations/ConnectionsAnimation';
import { SpecsAnimation } from '../animations/SpecsAnimation';

export type ExplanationType = 'connections' | 'specs';

interface FeatureExplanationModalProps {
    isOpen: boolean;
    onClose: () => void;
    type: ExplanationType;
}

export const FeatureExplanationModal: React.FC<FeatureExplanationModalProps> = ({
    isOpen,
    onClose,
    type
}) => {
    const { t } = useTranslation('common');

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto z-[60]">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform rounded-2xl bg-white dark:bg-neutral-900 shadow-xl transition-all flex flex-col max-h-[90vh] border border-transparent dark:border-neutral-800">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 shrink-0">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shadow-sm ${type === 'connections'
                                            ? 'bg-accent-primary/5 dark:bg-accent-primary/10 text-accent-primary border-accent-primary/10 dark:border-accent-primary/20'
                                            : 'bg-emerald-500/5 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/10 dark:border-emerald-500/20'}`}>
                                            {type === 'connections' ? <Zap className="w-4 h-4" /> : <Filter className="w-4 h-4" />}
                                        </div>
                                        <div className="text-left">
                                            <h3 className="text-sm font-bold text-neutral-900 dark:text-neutral-100 uppercase tracking-wider">
                                                {type === 'connections'
                                                    ? t('featureExplanation.connections.title', 'Board Connections')
                                                    : t('featureExplanation.specs.title', 'Board Specifications')
                                                }
                                            </h3>
                                            <p className="text-[10px] text-neutral-400 dark:text-neutral-500 font-bold uppercase tracking-widest mt-1">
                                                {t('featureExplanation.subtitle', 'Understanding the flow')}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={onClose}
                                        className="h-8 w-8 flex items-center justify-center text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Body Content */}
                                <div className="p-6 overflow-y-auto flex-1">
                                    <div className="space-y-6">
                                        {/* Animation Section */}
                                        <div className="rounded-xl overflow-hidden border border-neutral-100 dark:border-neutral-800 shadow-sm bg-neutral-50 dark:bg-neutral-900/50">
                                            {type === 'connections' ? <ConnectionsAnimation /> : <SpecsAnimation />}
                                        </div>

                                        {/* Description Section */}
                                        <div className="bg-neutral-50 dark:bg-neutral-800/50 rounded-xl p-5 border border-neutral-100 dark:border-neutral-800/50">
                                            <div className="flex gap-4">
                                                <div className={`mt-1 flex-shrink-0 w-1.5 h-1.5 rounded-full ${type === 'connections' ? 'bg-accent-primary' : 'bg-emerald-500'}`} />
                                                <p className="text-sm text-neutral-600 dark:text-neutral-300 font-medium leading-relaxed text-left">
                                                    {type === 'connections'
                                                        ? t('featureExplanation.connections.description', 'Connections made to a single source which is the board and data drown out of them to show financial flow centralized in the board.')
                                                        : t('featureExplanation.specs.description', "It's a proxy from the connection, it gets data from the connections happening in the board and creates another transformed source of data and directly fed to the board.")
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="px-6 py-4 border-t border-neutral-100 dark:border-neutral-800 flex justify-end shrink-0">
                                    <button
                                        onClick={onClose}
                                        className="h-9 px-6 flex items-center justify-center text-sm font-semibold text-white bg-accent-primary hover:bg-accent-primary/90 rounded-lg transition-colors shadow-sm"
                                    >
                                        {t('actions.gotIt', "Got it, thanks!")}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
